import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

export interface CustomerProfile {
  name: string;
  phone: string;
  street: string;
  postcode: string;
  city: string;
  email: string;
  latitude?: number;
  longitude?: number;
}

interface SessionData {
  sessionToken: string;
  uid: string;
  phone: string;
  email?: string;
  isNewUser: boolean;
  verified_at: number;
  profile?: {
    name?: string;
    phone?: string;
    email?: string;
    street?: string;
    postcode?: string;
    city?: string;
    latitude?: number;
    longitude?: number;
  };
}

interface AuthContextType {
  user: User | null;
  session: SessionData | null; // OTP session (non-Firebase auth)
  profile: CustomerProfile | null;
  loading: boolean;
  isAuthenticated: boolean; // true if either Firebase user OR OTP session
  setProfile: (p: CustomerProfile | null) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  isAuthenticated: false,
  setProfile: () => {},
  logout: () => {},
});

const SESSION_KEY = 'sushiki_session';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<SessionData | null>(() => {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Session expires after 24h
        if (parsed.verified_at && Date.now() - parsed.verified_at < 24 * 60 * 60 * 1000) {
          return parsed;
        }
        localStorage.removeItem(SESSION_KEY);
      }
    } catch {}
    return null;
  });
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Load profile from Firestore for a given UID
  const loadProfile = useCallback(async (uid: string, phoneNumber?: string) => {
    try {
      const snap = await getDoc(doc(db, 'customers', uid));
      if (snap.exists()) {
        const d = snap.data();
        setProfile({
          name: d.name || '',
          phone: d.phone || phoneNumber || '',
          street: d.street || '',
          postcode: d.postcode || '',
          city: d.city || '',
          email: d.email || '',
        });
      } else {
        setProfile({
          name: '',
          phone: phoneNumber || '',
          street: '',
          postcode: '',
          city: '',
          email: '',
        });
      }
    } catch {
      setProfile({
        name: '',
        phone: phoneNumber || '',
        street: '',
        postcode: '',
        city: '',
        email: '',
      });
    }
  }, []);

  // Listen for Firebase Auth changes
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        await loadProfile(firebaseUser.uid, firebaseUser.phoneNumber || '');
      } else if (!session) {
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, [session, loadProfile]);

  // Load profile for OTP session (non-Firebase auth) — use session data directly.
  // Merge both session root fields (phone, email) AND session.profile fields so
  // nothing gets lost regardless of which login path (phone OTP or email OTP) was used.
  useEffect(() => {
    if (session && !user) {
      const p = session.profile || {};
      setProfile({
        name: p.name || '',
        phone: p.phone || session.phone || '',
        street: p.street || '',
        postcode: p.postcode || '',
        city: p.city || '',
        email: p.email || session.email || '',
        latitude: p.latitude || undefined,
        longitude: p.longitude || undefined,
      });
      setLoading(false);
    }
  }, [session, user]);

  // Listen for session changes from AuthPage
  useEffect(() => {
    const handleAuthChanged = () => {
      try {
        const stored = localStorage.getItem(SESSION_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          setSession(parsed);
        }
      } catch {}
    };
    window.addEventListener('sushiki-auth-changed', handleAuthChanged);
    return () => window.removeEventListener('sushiki-auth-changed', handleAuthChanged);
  }, []);

  const logout = useCallback(() => {
    auth.signOut().catch(() => {});
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    setUser(null);
    setProfile(null);
  }, []);

  const isAuthenticated = !!(user || session);

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, isAuthenticated, setProfile, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Keep useAuth backwards-compatible: user is real Firebase user OR a mock for session
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');

  // Create a compatible "user" object from session for components that check user.uid/email
  const effectiveUser = ctx.user || (ctx.session ? {
    uid: ctx.session.uid,
    phoneNumber: ctx.session.phone || ctx.profile?.phone || null,
    email: ctx.session.email || ctx.profile?.email || null,
    displayName: ctx.profile?.name || null,
  } as any : null);

  return {
    user: effectiveUser,
    profile: ctx.profile,
    loading: ctx.loading,
    setProfile: ctx.setProfile,
    isAuthenticated: ctx.isAuthenticated,
    logout: ctx.logout,
  };
}
